'use client';

import { motion, type PanInfo } from 'framer-motion';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Toast, ToastAction, NotificationVariant } from '@/lib/stores/notification-store';
import { useNotificationStore } from '@/lib/stores/notification-store';
import { cn } from '@/lib/utils';
import { Button } from './button';

// ============================================================================
// CONSTANTS
// ============================================================================

const VARIANT_CONFIG: Record<
  NotificationVariant,
  {
    Icon: typeof CheckCircle;
    iconClassName: string;
    bgClassName: string;
    borderClassName: string;
    progressClassName: string;
    textClassName: string;
  }
> = {
  success: {
    Icon: CheckCircle,
    iconClassName: 'text-white',
    bgClassName: 'bg-green-500',
    borderClassName: 'border-green-600',
    progressClassName: 'bg-green-600',
    textClassName: 'text-white',
  },
  error: {
    Icon: XCircle,
    iconClassName: 'text-white',
    bgClassName: 'bg-red-500',
    borderClassName: 'border-red-600',
    progressClassName: 'bg-red-600',
    textClassName: 'text-white',
  },
  warning: {
    Icon: AlertTriangle,
    iconClassName: 'text-white',
    bgClassName: 'bg-orange-500',
    borderClassName: 'border-orange-600',
    progressClassName: 'bg-orange-600',
    textClassName: 'text-white',
  },
  info: {
    Icon: Info,
    iconClassName: 'text-white',
    bgClassName: 'bg-blue-500',
    borderClassName: 'border-blue-600',
    progressClassName: 'bg-blue-600',
    textClassName: 'text-white',
  },
};

const SWIPE_THRESHOLD = 100; // px
const SWIPE_VELOCITY_THRESHOLD = 500; // px/s

// ============================================================================
// COMPONENT
// ============================================================================

export interface ToastProps {
  toast: Toast;
}

export function ToastComponent({ toast }: ToastProps) {
  const hideToast = useNotificationStore((state) => state.hideToast);
  const [progress, setProgress] = useState(100);

  const { Icon, iconClassName, bgClassName, borderClassName, progressClassName, textClassName } =
    VARIANT_CONFIG[toast.variant];

  // Progress bar animation
  useEffect(() => {
    if (!toast.duration || toast.duration <= 0) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / (toast.duration || 5000)) * 100);
      setProgress(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [toast.duration]);

  // Handle swipe to dismiss
  function handleDragEnd(_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) {
    const shouldDismiss =
      Math.abs(info.offset.x) > SWIPE_THRESHOLD ||
      Math.abs(info.velocity.x) > SWIPE_VELOCITY_THRESHOLD;

    if (shouldDismiss) {
      hideToast(toast.id);
    }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      drag={toast.dismissible ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragEnd={handleDragEnd}
      className={cn(
        'relative flex w-full max-w-sm gap-3 rounded-lg border p-4 shadow-lg',
        bgClassName,
        borderClassName,
        'backdrop-blur-sm',
        toast.dismissible && 'cursor-grab active:cursor-grabbing'
      )}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Icon */}
      <div className="flex-shrink-0">
        <Icon className={cn('h-5 w-5', iconClassName)} aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="flex-1 space-y-1">
        {toast.title && (
          <p className={cn('text-sm font-semibold', textClassName)}>{toast.title}</p>
        )}
        <p className={cn('text-sm', textClassName, 'opacity-90')}>{toast.message}</p>

        {/* Actions */}
        {toast.actions && toast.actions.length > 0 && (
          <div className="flex gap-2 pt-2">
            {toast.actions.map((action, index) => (
              <Button
                key={index}
                onClick={() => {
                  action.onClick();
                  hideToast(toast.id);
                }}
                variant={
                  action.variant === 'primary'
                    ? 'default'
                    : action.variant === 'secondary'
                      ? 'secondary'
                      : 'ghost'
                }
                size="sm"
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Dismiss button */}
      {toast.dismissible && (
        <button
          onClick={() => hideToast(toast.id)}
          className="flex-shrink-0 rounded-full p-1 hover:bg-white/20 transition-colors"
          aria-label="Fechar notificação"
          type="button"
        >
          <X className={cn('h-4 w-4', textClassName)} />
        </button>
      )}

      {/* Progress bar */}
      {toast.duration && toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-lg" style={{ background: "var(--p-border-color)" }}>
          <motion.div
            className={cn('h-full', progressClassName)}
            style={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
      )}
    </motion.div>
  );
}
